import { Tag as TagIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import useDebounce from "common/hooks/useDebounce";
import type { ConversationParticipant } from "common/types";

import { INBOX_VARIABLES } from "../../../constants";

interface MessageVariablesProps {
  participant: ConversationParticipant;
  isMessagingDisabled: boolean;
  insertText: (text: string) => void;
}

// Remove emojis from a string
function removeEmojis(text: string): string {
  return text
    .replace(
      /[\u{1F300}-\u{1F9FF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F1E0}-\u{1F1FF}]/gu,
      "",
    )
    .trim();
}

export default function MessageVariables({
  participant,
  isMessagingDisabled,
  insertText,
}: MessageVariablesProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  // Memoize the filtered variables to prevent unnecessary recalculations
  const filteredVariables = useMemo(() => {
    const searchTermLower = debouncedSearchTerm.toLowerCase();
    return INBOX_VARIABLES.filter(({ label }) =>
      label.toLowerCase().includes(searchTermLower),
    );
  }, [debouncedSearchTerm]);

  return (
    <DropdownMenu
      onOpenChange={(isOpen) => {
        if (!isOpen) setSearchTerm("");
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost-black"
          className="data-[state=open]:bg-black-100"
          intent="iconOnly"
          size="xs"
          aria-label="Variables"
          disabled={isMessagingDisabled}
        >
          <TagIcon />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
        {INBOX_VARIABLES.length > 4 && (
          <MenuSearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}

        <DropdownMenuList>
          {filteredVariables.length ? (
            filteredVariables.map(({ label, getValue, icon: Icon }) => {
              const value = getValue(participant);
              if (!value) return null;

              const cleanValue = removeEmojis(value);
              if (!cleanValue) return null;

              return (
                <DropdownMenuItem
                  key={label}
                  onClick={() => insertText(`${cleanValue} `)}
                >
                  <Icon />
                  {label}
                </DropdownMenuItem>
              );
            })
          ) : (
            <p className="mx-4 truncate py-2 text-center text-black-500">
              No results for{" "}
              <span className="text-black-950">&quot;{searchTerm}&quot;</span>
            </p>
          )}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
